import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import SettingProperties from "./setting-properties-text.mdx";
import NamingTip from "./naming-tip.mdx";
import Intro from "./intro.mdx";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <Intro mdxType="Intro" />
    <pre><code parentName="pre" {...{
        "className": "language-react-native"
      }}>{`posthog.capture('user signed up')
`}</code></pre>
    <NamingTip mdxType="NamingTip" />
    <SettingProperties mdxType="SettingProperties" />
    <pre><code parentName="pre" {...{
        "className": "language-react-native"
      }}>{`posthog.capture('user signed up', {
    login_type: "email",
    is_free_trial: true
})
`}</code></pre>
    <h2 {...{
      "id": "autocapture"
    }}>{`Autocapture`}</h2>
    <p>{`PostHog autocapture automatically tracks the following events for you:`}</p>
    <ul>
      <li parentName="ul"><strong parentName="li">{`Application Opened`}</strong>{` - when the app is opened from a closed state`}</li>
      <li parentName="ul"><strong parentName="li">{`Application Became Active`}</strong>{` - when the app comes to the foreground (e.g. from the app switcher)`}</li>
      <li parentName="ul"><strong parentName="li">{`Application Backgrounded`}</strong>{` - when the app is sent to the background by the user`}</li>
      <li parentName="ul"><strong parentName="li">{`$screen`}</strong>{` - when the user navigates (if using `}<inlineCode parentName="li">{`@react-navigation/native`}</inlineCode>{` or `}<inlineCode parentName="li">{`react-native-navigation`}</inlineCode>{`)`}</li>
      <li parentName="ul"><strong parentName="li">{`$autocapture`}</strong>{` - touch events when the user interacts with the screen`}</li>
    </ul>
    <p>{`With autocapture, all touch events for children of `}<inlineCode parentName="p">{`PosthogProvider`}</inlineCode>{` are tracked, capturing a snapshot of the view hierarchy at that point. This enables you to create `}<a parentName="p" {...{
        "href": "/docs/product-analytics/insights"
      }}>{`insights`}</a>{` in PostHog without having to add custom events.`}</p>
    <p>{`PostHog will try to generate a sensible name for the touched element based on the React component `}<inlineCode parentName="p">{`displayName`}</inlineCode>{` or `}<inlineCode parentName="p">{`name`}</inlineCode>{` but you can force this to something reliable (and also clearly marked for PostHog tracking) using the `}<inlineCode parentName="p">{`ph-label`}</inlineCode>{` prop.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-react-native"
      }}>{`<View ph-label="my-special-label"></View>
`}</code></pre>
    <p>{`If there are elements you don't want to be captured, you can add the `}<inlineCode parentName="p">{`ph-no-capture`}</inlineCode>{` property like so. If this property is found anywhere in the view hierarchy, the entire touch event is ignored.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-react-native"
      }}>{`<View ph-no-capture>Sensitive view here</View>
`}</code></pre>
    <h4 {...{
      "id": "tracking-screen-views-and-touches-with-react-navigationnative"
    }}>{`Tracking Screen views and touches with `}<inlineCode parentName="h4">{`@react-navigation/native`}</inlineCode>{`:`}</h4>
    <pre><code parentName="pre" {...{
        "className": "language-react-native"
      }}>{`// App.(js|ts)

import { PostHogProvider } from 'posthog-react-native'
import { NavigationContainer } from '@react-navigation/native'

export function App() {
    return (
        <NavigationContainer>
            <PostHogProvider apiKey="<ph_project_api_key>" autocapture>
                {/* Rest of app */}
            </PostHogProvider>
        </NavigationContainer>
    )
}
`}</code></pre>
    <h4 {...{
      "id": "tracking-screen-views-and-touches-with-react-native-navigation"
    }}>{`Tracking Screen views and touches with `}<inlineCode parentName="h4">{`react-native-navigation`}</inlineCode>{`:`}</h4>
    <pre><code parentName="pre" {...{
        "className": "language-react-native"
      }}>{`import PostHog, { PostHogProvider } from 'posthog-react-native'
import { Navigation } from 'react-native-navigation';

export const posthogAsync = PostHog.initAsync('<ph_project_api_key>');

// Simplify the wrapping of your Screens with a shared PostHogProvider
export const SharedPostHogProvider = (props: any) => {
  return (
    <PostHogProvider client={posthogAsync} autocapture={{
      captureLifecycleEvents: false,  // Lifecycle events are handled differently for react-native-navigation
      captureTouches: true,
    }}>
      {props.children}
    </PostHogProvider>
  );
};

export const MyScreen = () => {
  return (
    // Every screen needs to be wrapped with this provider if you want to capture touches or use the hook \`usePostHog()\` 
    <SharedPostHogProvider>
      <View>
        ...
      </View>
    </SharedPostHogProvider>
  );
};

Navigation.registerComponent('Screen', () => MyScreen);

Navigation.events().registerAppLaunchedListener(async () => {
  (await posthogAsync).initReactNativeNavigation({
    navigation: {
      // (Optional) Set the name based on the route. Defaults to the route name.
      routeToName: (name, properties) => name,
      // (Optional) Tracks all passProps as properties. Defaults to undefined
      routeToProperties: (name, properties) => properties,
    },
  });
});
`}</code></pre>
    <h3 {...{
      "id": "autocapture-configuration"
    }}>{`Autocapture configuration`}</h3>
    <p>{`You can tweak how autocapture works by passing custom options.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-react-native"
      }}>{`<PostHogProvider apiKey="<ph_project_api_key>" autocapture={{
    captureTouches: true, // If you don't want to capture touch events set this to false
    captureLifecycleEvents: true, // If you don't want to capture the Lifecycle Events (e.g. Application Opened) set this to false
    captureScreens: true, // If you don't want to capture screen events set this to false
    ignoreLabels: [], // Any labels here will be ignored from the stack in touch events
    customLabelProp: "ph-label",
    noCaptureProp: "ph-no-capture",
    propsToCapture = ["testID"], // Limit which props are captured. By default, identifiers and text content are captured.

    navigation: {
        // By default only the Screen name is tracked but it is possible to track the
        // params or modify the name by intercepting theautocapture like so
        routeToName: (name, params) => {
            if (params.id) return \`\${name}/\${params.id}\`
            return name
        },
        routeToParams: (name, params) => {
            if (name === "SensitiveScreen") return undefined
            return params
        },
    }
}}>
    ...
</PostHogProvider>
`}</code></pre>
    <h3 {...{
      "id": "capturing-screen-views"
    }}>{`Capturing screen views`}</h3>
    <p>{`With the `}<inlineCode parentName="p">{`PostHogProvider`}</inlineCode>{`, screen tracking is automatically captured if the `}<inlineCode parentName="p">{`autocapture`}</inlineCode>{` property is used. Currently only `}<a parentName="p" {...{
        "href": "https://reactnavigation.org/docs/getting-started/"
      }}>{`@react-navigation/native`}</a>{` is supported by autocapture and it is important that the `}<inlineCode parentName="p">{`PostHogProvider`}</inlineCode>{` is configured as a child of the `}<inlineCode parentName="p">{`NavigationContainer`}</inlineCode>{`.`}</p>
    <p>{`If you want to manually send a new screen capture event, use the `}<inlineCode parentName="p">{`screen`}</inlineCode>{` function. This function requires a `}<inlineCode parentName="p">{`name`}</inlineCode>{`. You may also pass in an optional `}<inlineCode parentName="p">{`properties`}</inlineCode>{` object.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`posthog.screen('dashboard', {
    background: 'blue',
    hero: 'superhog',
})
`}</code></pre>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      